import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { OpenInNew as OpenIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { Line, LineChart } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { AdminAlert, AdminAlerts, PlatformRouteRow, PlatformRoutes, performanceApi } from '../services/performanceApi';
import { usePersistentState } from '../utils/usePersistentState';
import { SectionTitle, WarningChips, fmtMs, fmtNum, fmtPct, fmtS, severityColor } from '../components/performance/common';
import { Batch, BatchTargetRow, ModelGroups, PlatformRouteTests, Schedule, testsApi } from '../services/testsApi';
import { PICK_LABELS, PickCell, Score, forgetModelGroups, modelLabel } from '../components/tests/common';
import { ModelGroupsDialog } from '../components/tests/ModelGroupsDialog';
import { PlatformTestDialog } from '../components/tests/PlatformTestDialog';
import { ScheduleDialog, scheduleText } from '../components/tests/ScheduleDialog';
import { runUrl } from '../components/tests/TargetTests';
import { currentAccuracy } from '../components/tests/AccuracyTab';
import { DynamicRoutesSection, StatusChip } from '../components/platform/DynamicRoutesSection';
import { platformRoutesApi } from '../services/platformRoutesApi';

const PLATFORM_WINDOWS = [1, 7, 30];

const Sparkline: React.FC<{ points: { p95_ms: number | null }[] }> = ({ points }) =>
  points.length < 2 ? (
    <Typography variant="caption" color="text.secondary">
      –
    </Typography>
  ) : (
    <LineChart width={90} height={26} data={points}>
      <Line dataKey="p95_ms" stroke="#1976d2" dot={false} strokeWidth={1.5} connectNulls isAnimationActive={false} />
    </LineChart>
  );

const headroomColor = (r: PlatformRouteRow) =>
  r.headroom_s === null ? 'text.secondary' : r.headroom_s < 2 ? 'error.main' : r.headroom_s < 5 ? 'warning.main' : 'success.main';

const functionUrl = (customerId: string | undefined, customerTag: string | null | undefined, functionId: string | null | undefined) => {
  if (!functionId) return null;
  const params = new URLSearchParams({ tab: 'performance' });
  if (customerId) params.set('customer_id', customerId);
  if (customerTag) params.set('customer_tag', customerTag);
  return `/dashboard/function-runs/${functionId}?${params}`;
};

const stickyActionCell = {
  position: 'sticky',
  right: 0,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderLeft: 1,
  borderLeftColor: 'divider',
  boxShadow: '-6px 0 6px -6px rgba(0, 0, 0, 0.25)',
} as const;

const markedBy = (r: PlatformRouteRow) => {
  const who = [r.mark?.created_by, r.mark?.created_at && new Date(r.mark.created_at).toLocaleDateString()].filter(Boolean).join(', ');
  return `Marked as a platform function by an admin${who ? ` (${who})` : ''}. Change it from the function's Performance page.`;
};

const ago = (iso: string | null) => {
  if (!iso) return '';
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return min < 1 ? 'just now' : min < 60 ? `${min} min ago` : `${Math.floor(min / 60)} h ${min % 60} min ago`;
};

/** Share of a route's run that's done: finished runs count fully, queued ones as nothing. */
const targetShare = (t: BatchTargetRow) => {
  if (!['queued', 'running'].includes(t.status)) return 1;
  const p = t.progress || {};
  return p.total ? Math.min(1, (p.done || 0) / p.total) : 0;
};

const RECENT_BATCH_MS = 7 * 24 * 3600 * 1000;

/** Routes in a batch that stopped at their credit cap, with roughly what finishing them would cost. */
const cappedTargets = (batch: Batch) => {
  const targets = (batch.summary.targets || []).filter(t => t.status === 'completed' && t.progress?.capped && (t.progress.total || 0) > (t.progress.done || 0));
  const credits = targets.reduce((n, t) => {
    const done = t.progress?.done || 0;
    return n + (done ? (Number(t.credits_used || 0) / done) * ((t.progress?.total || 0) - done) : 0);
  }, 0);
  return { targets, credits };
};

/** A running batch: overall progress, then every route with its own status and a link to its live run page. */
const BatchProgress: React.FC<{ batch: Batch; updatedAt: Date | null; onCancel: () => void }> = ({ batch, updatedAt, onCancel }) => {
  const targets = batch.summary.targets || [];
  const runs = batch.summary.runs || targets.length;
  const finished = batch.summary.finished ?? 0;
  const pct = targets.length ? (100 * targets.reduce((n, t) => n + targetShare(t), 0)) / targets.length : 0;
  const results = targets.reduce((n, t) => n + (t.progress?.done || 0), 0);
  const credits = targets.reduce((n, t) => n + Number(t.credits_used || 0), 0);
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="body2" fontWeight={600}>
          {batch.label}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {finished} of {runs} routes done · {results} results · {fmtNum(Math.round(credits * 10) / 10)} credits · started {ago(batch.created_at)}
          {updatedAt ? ` · updated ${updatedAt.toLocaleTimeString()}` : ''}
        </Typography>
        <Button size="small" color="warning" onClick={onCancel}>
          Cancel
        </Button>
      </Box>
      <LinearProgress variant="determinate" value={pct} sx={{ mb: 1 }} />
      <Stack spacing={0.75}>
        {targets.map(t => {
          const p = t.progress || {};
          const active = t.status === 'running';
          let detail: string;
          if (t.status === 'queued') detail = 'Queued: starts as soon as another route finishes (a few run at once)';
          else if (active && !p.total) detail = 'Starting…';
          else if (active) detail = `${p.done || 0} of ${p.total} results`;
          else if (t.status === 'completed' && p.capped) detail = `Stopped at the credit cap: ${p.done || 0} of ${p.total || '?'} results`;
          else detail = t.status === 'completed' ? `Done: ${p.done || 0} results` : t.status[0].toUpperCase() + t.status.slice(1);
          const extras = [p.failed ? `${p.failed} failed` : '', p.skipped ? `${p.skipped} skipped` : '', t.credits_used ? `${fmtNum(Math.round(Number(t.credits_used) * 10) / 10)} credits` : '']
            .filter(Boolean)
            .join(' · ');
          return (
            <Box key={t.run_id} sx={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 260px) 120px 1fr', alignItems: 'center', gap: 1.5 }}>
              <Link component={RouterLink} to={runUrl(t.run_id, batch.customer_id)} variant="body2" noWrap title="Open the live run page">
                {t.target_name || t.target_id}
              </Link>
              <LinearProgress
                variant={active && !p.total ? 'indeterminate' : 'determinate'}
                value={100 * targetShare(t)}
                color={t.status === 'failed' ? 'error' : t.status === 'cancelled' ? 'warning' : t.status === 'completed' ? 'success' : 'primary'}
              />
              <Tooltip title={t.error || ''}>
                <Typography variant="caption" color={t.status === 'failed' ? 'error' : 'text.secondary'} noWrap>
                  {detail}
                  {extras ? ` · ${extras}` : ''}
                </Typography>
              </Tooltip>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

export const AdminPlatformRoutes: React.FC = () => {
  const { getIdToken } = useAuth();
  const navigate = useNavigate();
  const api = useMemo(() => performanceApi(getIdToken), [getIdToken]);
  const [days, setDays] = usePersistentState('platformRoutes.days', 7, PLATFORM_WINDOWS);
  const [platformCustomer, setPlatformCustomer] = useState<string | null>(null);
  const [data, setData] = useState<PlatformRoutes | null>(null);
  const [alerts, setAlerts] = useState<AdminAlerts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const tests = useMemo(() => testsApi(getIdToken), [getIdToken]);
  const [testResults, setTestResults] = useState<PlatformRouteTests[]>([]);
  const [testBatches, setTestBatches] = useState<Batch[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [testOpen, setTestOpen] = useState(false);
  const [testNotice, setTestNotice] = useState<string | null>(null);
  const [testsReload, setTestsReload] = useState(0);
  const [testsUpdatedAt, setTestsUpdatedAt] = useState<Date | null>(null);
  const [schedule, setSchedule] = useState<Schedule | null | undefined>(undefined);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [cacheNotice, setCacheNotice] = useState<{ severity: 'success' | 'warning' | 'error'; text: string } | null>(null);
  const [groups, setGroups] = useState<ModelGroups | null>(null);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const platformAccount = data?.platform_customer?.customer_id || null;

  useEffect(() => {
    let cancelled = false;
    tests
      .modelGroups()
      .then(g => !cancelled && setGroups(g))
      .catch(() => !cancelled && setGroups(null));
    return () => {
      cancelled = true;
    };
  }, [tests, reload]);

  const setRouteGroup = (familyId: string, groupId: string | null) =>
    tests
      .setRouteGroup(familyId, groupId)
      .then(g => {
        setGroups(g);
        forgetModelGroups();
        setTestsReload(r => r + 1);
      })
      .catch(e => setCacheNotice({ severity: 'error', text: `Couldn't change the model group: ${e instanceof Error ? e.message : String(e)}` }));

  const clearModelCache = async () => {
    setClearingCache(true);
    try {
      const r = await platformRoutesApi(getIdToken).clearModelCache();
      setCacheNotice(
        r.configured
          ? { severity: 'success', text: `Cleared the platform's cached models for ${r.functions.length} platform functions (${r.cleared} cached entries). Platform calls use the current models within a minute.` }
          : { severity: 'warning', text: "This environment has no platform cache to clear (PLATFORM_REDIS_URL isn't set)." },
      );
    } catch (e) {
      setCacheNotice({ severity: 'error', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setClearingCache(false);
    }
  };
  const platformTests = useMemo(() => (platformAccount ? testsApi(getIdToken, platformAccount) : null), [getIdToken, platformAccount]);

  useEffect(() => {
    let cancelled = false;
    tests
      .platformResults()
      .then(d => {
        if (cancelled) return;
        setTestResults(d.routes);
        setTestBatches(d.batches);
        setTestsUpdatedAt(new Date());
      })
      .catch(() => !cancelled && setTestResults([]));
    return () => {
      cancelled = true;
    };
  }, [tests, reload, testsReload]);

  const batchRunning = testBatches.some(b => b.status === 'running');
  useEffect(() => {
    if (!batchRunning) return;
    const t = setInterval(() => setTestsReload(r => r + 1), 8000);
    return () => clearInterval(t);
  }, [batchRunning]);

  useEffect(() => {
    if (!platformTests) return;
    let cancelled = false;
    platformTests
      .schedules()
      .then(d => !cancelled && setSchedule(d.schedules.find(s => s.scope === 'platform') || null))
      .catch(() => !cancelled && setSchedule(null));
    return () => {
      cancelled = true;
    };
  }, [platformTests, reload]);

  const routeKey = (customerId: string | undefined, name: string) => `${customerId}:${name}`;
  const testsByKey = useMemo(() => new Map(testResults.map(t => [routeKey(t.customer_id, t.name || ''), t])), [testResults]);
  const toggleSelected = (key: string) =>
    setSelected(s => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([api.platformRoutes(days, platformCustomer), api.alerts()])
      .then(([routes, a]) => {
        if (cancelled) return;
        setData(routes);
        setAlerts(a);
      })
      .catch(e => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [api, days, platformCustomer, reload]);

  const openFunction = (customerId: string | undefined, customerTag: string | null | undefined, functionId: string | null | undefined) => {
    const url = functionUrl(customerId, customerTag, functionId);
    if (url) navigate(url);
  };

  const openAlert = (a: AdminAlert) => {
    if (a.function_id) openFunction(a.customer_id, a.customer_tag, a.function_id);
    else if (a.chain_id) navigate(`/dashboard/chain-performance/${a.chain_id}${a.customer_id ? `?customer_id=${encodeURIComponent(a.customer_id)}` : ''}`);
    else if (a.model) navigate('/dashboard/models');
  };

  const platformRows = data?.routes.filter(r => r.kind !== 'regional_search') ?? [];
  const regionalRows = data?.routes.filter(r => r.kind === 'regional_search') ?? [];

  const selectable = (rows: PlatformRouteRow[]) => rows.filter(r => r.function_id);
  const allSelected = (rows: PlatformRouteRow[]) => {
    const keys = selectable(rows).map(r => routeKey(r.customer_id, r.function_name));
    return keys.length > 0 && keys.every(k => selected.has(k));
  };

  const table = (rows: PlatformRouteRow[], regional: boolean) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {!regional && (
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allSelected(rows)}
                  indeterminate={!allSelected(rows) && selectable(rows).some(r => selected.has(routeKey(r.customer_id, r.function_name)))}
                  onChange={() => {
                    const keys = selectable(rows).map(r => routeKey(r.customer_id, r.function_name));
                    setSelected(allSelected(rows) ? new Set() : new Set(keys));
                  }}
                />
              </TableCell>
            )}
            <TableCell>{regional ? 'Function (customer)' : 'Function / platform tool'}</TableCell>
            {!regional && (
              <>
                <TableCell>
                  <Tooltip title="The models this route tests on when a test uses each route's model group. Auto follows the route's kind; manage groups with Model groups.">
                    <span style={{ cursor: 'help' }}>Model group</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Tested accuracy (0–100) of the current default model, from the latest completed test">
                    <span style={{ cursor: 'help' }}>Test accuracy</span>
                  </Tooltip>
                </TableCell>
                {(['recommended', 'quality', 'budget'] as const).map(k => (
                  <TableCell key={k}>
                    <Tooltip title={PICK_LABELS[k].help}>
                      <span style={{ cursor: 'help' }}>{PICK_LABELS[k].label}</span>
                    </Tooltip>
                  </TableCell>
                ))}
              </>
            )}
            <TableCell align="right">Runs</TableCell>
            <TableCell align="right">
              <Tooltip title="Average over the window, and runs in the last hour">
                <span style={{ cursor: 'help' }}>Per min / last hr</span>
              </Tooltip>
            </TableCell>
            <TableCell align="right">Success</TableCell>
            <TableCell align="right">p50 / p95 / p99</TableCell>
            <TableCell>p95 trend</TableCell>
            {!regional && (
              <TableCell align="right">
                <Tooltip title={`Runs slower than ${Math.round((data?.near_timeout_ratio ?? 0.8) * 100)}% of the platform's deadline`}>
                  <span style={{ cursor: 'help' }}>Near platform timeout</span>
                </Tooltip>
              </TableCell>
            )}
            <TableCell align="right">
              <Tooltip title="EasyAI time limit now, and how far it sits under the platform's Go timeout">
                <span style={{ cursor: 'help' }}>Time limit {regional ? '' : '/ headroom'}</span>
              </Tooltip>
            </TableCell>
            <TableCell align="right">Backup</TableCell>
            <TableCell>Self-improvement</TableCell>
            <TableCell>Warnings</TableCell>
            <TableCell sx={stickyActionCell} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={regional ? 12 : 18} align="center">
                Nothing here yet
              </TableCell>
            </TableRow>
          ) : (
            rows.map(r => {
              const key = routeKey(r.customer_id, r.function_name);
              const t = testsByKey.get(key);
              return (
              <TableRow
                key={key}
                hover
                onClick={() => openFunction(r.customer_id, r.customer_tag, r.function_id)}
                sx={theme => ({
                  cursor: r.function_id ? 'pointer' : 'default',
                  '&:hover > td:last-of-type': {
                    backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
                  },
                })}
              >
                {!regional && (
                  <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                    <Checkbox size="small" disabled={!r.function_id} checked={selected.has(key)} onChange={() => toggleSelected(key)} />
                  </TableCell>
                )}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {r.function_id ? (
                      <Link
                        component={RouterLink}
                        to={functionUrl(r.customer_id, r.customer_tag, r.function_id) ?? ''}
                        onClick={e => e.stopPropagation()}
                        variant="body2"
                        underline="hover"
                        sx={{ fontWeight: 500 }}
                      >
                        {r.function_name}
                      </Link>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {r.function_name}
                      </Typography>
                    )}
                    {r.custom && !r.mark?.slug && (
                      <Tooltip title={markedBy(r)}>
                        <Chip size="small" variant="outlined" color="secondary" label="custom" sx={{ height: 18, fontSize: 11 }} />
                      </Tooltip>
                    )}
                    {r.mark?.slug && (
                      <Tooltip title={`Dynamic route /napi/tools/${r.mark.slug}`}>
                        <span>
                          <StatusChip status={r.mark.status ?? 'off'} />
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {regional
                      ? r.customer_tag || r.customer_id
                      : `${r.platform_tool}${r.custom && r.customer_id !== data?.platform_customer?.customer_id ? ` · ${r.customer_tag || r.customer_id}` : ''} · ${r.default_model || 'no model'}`}
                  </Typography>
                </TableCell>
                {!regional && (
                  <>
                    <TableCell onClick={e => e.stopPropagation()} sx={{ minWidth: 150 }}>
                      {t?.group && groups ? (
                        <Tooltip title={t.group.models.length ? t.group.models.map(modelLabel).join(', ') : 'No models in this group'} placement="top">
                          <Select
                            size="small"
                            variant="standard"
                            value={t.group.auto ? '' : t.group.id}
                            displayEmpty
                            onChange={e => setRouteGroup(t.family_id, (e.target.value as string) || null)}
                            renderValue={v => (v ? groups.groups.find(g => g.id === v)?.name || v : `${t.group?.name} (auto)`)}
                            sx={{ fontSize: 13 }}
                          >
                            <MenuItem value="">Auto, from the route's kind</MenuItem>
                            {groups.groups.map(g => (
                              <MenuItem key={g.id} value={g.id}>
                                {g.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {t?.group ? `${t.group.name}${t.group.auto ? ' (auto)' : ''}` : '–'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      {t?.tested_run_id ? (
                        <Tooltip title={`${t.cases} cases · tested ${t.tested ? new Date(t.tested.tested_at).toLocaleDateString() : ''}`}>
                          <Link component={RouterLink} to={runUrl(t.tested_run_id, t.customer_id)} underline="hover">
                            <Score value={currentAccuracy(t)} />
                          </Link>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {t ? `${t.cases} cases` : 'no cases'}
                        </Typography>
                      )}
                    </TableCell>
                    {(['recommended', 'quality', 'budget'] as const).map(k => (
                      <TableCell key={k} sx={{ minWidth: 150 }}>
                        <PickCell pick={t?.tested?.picks[k]} current={t?.current_default ?? r.default_model} />
                      </TableCell>
                    ))}
                  </>
                )}
                <TableCell align="right">{fmtNum(r.runs)}</TableCell>
                <TableCell align="right">
                  {r.runs_per_min.toFixed(r.runs_per_min < 1 ? 2 : 1)} / {fmtNum(r.runs_last_hour)}
                </TableCell>
                <TableCell align="right">{fmtPct(r.success_rate)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {fmtMs(r.p50_ms)} / {fmtMs(r.p95_ms)} / {fmtMs(r.p99_ms)}
                </TableCell>
                <TableCell>
                  <Sparkline points={r.daily} />
                </TableCell>
                {!regional && (
                  <TableCell align="right">
                    {r.platform_timeout_s ? (
                      <Tooltip title={`${fmtNum(r.near_timeout)} runs over ${fmtS(r.platform_timeout_s * (data?.near_timeout_ratio ?? 0.8))} of the ${fmtS(r.platform_timeout_s)} deadline`}>
                        <Typography variant="body2" color={(r.near_timeout_share || 0) >= 0.01 ? 'error.main' : 'text.primary'}>
                          {fmtPct(r.near_timeout_share)}
                        </Typography>
                      </Tooltip>
                    ) : (
                      '–'
                    )}
                  </TableCell>
                )}
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title={r.time_limit_note || ''}>
                    <span>{r.time_limit_s ? fmtS(r.time_limit_s) : 'none'}</span>
                  </Tooltip>
                  {!regional && (
                    <Typography component="span" variant="body2" sx={{ color: headroomColor(r) }}>
                      {' / '}
                      {r.headroom_s !== null ? fmtS(r.headroom_s) : r.platform_timeout_s ? `${fmtS(r.platform_timeout_s)} (Go only)` : '–'}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{fmtPct(r.backup_share)}</TableCell>
                <TableCell>
                  {r.learning ? (
                    <Tooltip title={r.learning.last_cycle_at ? `Last cycle ${new Date(r.learning.last_cycle_at).toLocaleString()} (${r.learning.last_cycle_status})` : 'No cycles yet'}>
                      <Chip size="small" label={r.learning.enabled ? 'On' : 'Off'} color={r.learning.enabled ? 'success' : 'default'} variant="outlined" />
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      not set up
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <WarningChips warnings={r.warnings} max={2} />
                </TableCell>
                <TableCell sx={stickyActionCell}>
                  <Tooltip title="Open performance">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!r.function_id}
                        onClick={e => {
                          e.stopPropagation();
                          openFunction(r.customer_id, r.customer_tag, r.function_id);
                        }}
                      >
                        <OpenIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4">Platform Routes</Typography>
          <Typography variant="body2" color="text.secondary">
            Passio platform API calls that run through EasyAI functions: traffic, speed, and how close they get to the platform's own timeouts.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {data && data.candidates.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Platform account</InputLabel>
              <Select
                label="Platform account"
                value={platformCustomer ?? data.platform_customer?.customer_id ?? ''}
                onChange={e => setPlatformCustomer(e.target.value || null)}
              >
                {data.candidates.map(c => (
                  <MenuItem key={c.customer_id} value={c.customer_id}>
                    {c.customer_tag || c.customer_id} ({c.functions} functions)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)}>
            {PLATFORM_WINDOWS.map(d => (
              <ToggleButton key={d} value={d}>
                {d === 1 ? '24h' : `${d}d`}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <IconButton onClick={() => setReload(r => r + 1)} disabled={loading}>
            {loading ? <CircularProgress size={22} /> : <RefreshIcon />}
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {alerts && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <SectionTitle
            title="Alerts"
            help="All customers. Also in the daily digest email (Admin › Alert Emails)."
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Model down/degraded alerts are live and clear as soon as the model recovers. Model incidents stay for 24h after they end. Function and chain warnings
            cover the last 7 days against the 7 before, so they fade a few days after a fix; the &quot;Last 24h&quot; part shows whether it&apos;s still happening.
            {alerts.generated_at && ` Checked ${new Date(alerts.generated_at).toLocaleTimeString()}.`}
          </Typography>
          {alerts.alerts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nothing needs attention.
            </Typography>
          ) : (
            <Stack spacing={0.75} sx={{ maxHeight: 320, overflow: 'auto' }}>
              {alerts.alerts.map((a, i) => (
                <Box
                  key={i}
                  onClick={() => openAlert(a)}
                  sx={{ display: 'flex', gap: 1, alignItems: 'baseline', cursor: a.function_id || a.chain_id || a.model ? 'pointer' : 'default', '&:hover': { bgcolor: 'action.hover' }, px: 0.5, py: 0.25, borderRadius: 1 }}
                >
                  <Chip size="small" label={a.severity} color={severityColor(a.severity)} sx={{ minWidth: 72, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500, maxWidth: '40%' }}>
                    {a.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
                    {a.detail}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Paper>
      )}

      <DynamicRoutesSection reload={reload} onChanged={() => setReload(r => r + 1)} />

      {data && (
        <Box sx={{ opacity: loading ? 0.6 : 1 }}>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <SectionTitle
              title={`Platform functions${data.platform_customer ? ` (${data.platform_customer.customer_tag || data.platform_customer.customer_id})` : ''}`}
              help="The platform gives up after 20s for search terms, 55s for vision, 85s for kitchen and health tools, 45s for other text calls. Keep the time limit (and backup attempts) under that."
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Mark other functions as platform functions from their Performance page.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
              <Button size="small" variant="contained" disabled={!selected.size} onClick={() => setTestOpen(true)}>
                Test selected{selected.size ? ` (${selected.size})` : ''}
              </Button>
              <Button size="small" variant="outlined" disabled={!platformTests || schedule === undefined} onClick={() => setScheduleOpen(true)}>
                {schedule ? 'Weekly test' : 'Schedule weekly test'}
              </Button>
              <Button size="small" variant="outlined" onClick={() => setGroupsOpen(true)}>
                Model groups
              </Button>
              <Tooltip title="The platform keeps each platform function's default and allowed models for up to an hour. EasyAI clears that copy whenever a platform function is saved; use this if a model change still isn't showing. Platform calls pick it up within a minute.">
                <span>
                  <Button size="small" variant="outlined" disabled={clearingCache} onClick={clearModelCache}>
                    {clearingCache ? 'Clearing…' : 'Clear platform model cache'}
                  </Button>
                </span>
              </Tooltip>
              <Typography variant="caption" color="text.secondary">
                {schedule ? `${scheduleText(schedule)}${schedule.email_digest ? ', with digest' : ''}. ` : ''}
                Test results, picks and publishing are under{' '}
                <Link component={RouterLink} to="/dashboard/admin/insights?tab=accuracy">
                  Platform Insights › Accuracy & models
                </Link>
                .
              </Typography>
            </Box>
            {testNotice && (
              <Alert severity="success" onClose={() => setTestNotice(null)} sx={{ mb: 1.5 }}>
                {testNotice}
              </Alert>
            )}
            {cacheNotice && (
              <Alert severity={cacheNotice.severity} onClose={() => setCacheNotice(null)} sx={{ mb: 1.5 }}>
                {cacheNotice.text}
              </Alert>
            )}
            {testBatches
              .filter(b => b.status === 'running')
              .map(b => (
                <BatchProgress
                  key={b.id}
                  batch={b}
                  updatedAt={testsUpdatedAt}
                  onCancel={() =>
                    testsApi(getIdToken, b.customer_id)
                      .cancelBatch(b.id)
                      .then(() => setTestsReload(r => r + 1))
                      .catch(e => setTestNotice(`Couldn't cancel: ${e instanceof Error ? e.message : String(e)}`))
                  }
                />
              ))}
            {testBatches
              .filter(b => b.status === 'completed' && b.created_at && Date.now() - new Date(b.created_at).getTime() < RECENT_BATCH_MS)
              .map(b => ({ batch: b, ...cappedTargets(b) }))
              .filter(x => x.targets.length > 0)
              .map(({ batch: b, targets, credits }) => (
                <Alert
                  key={b.id}
                  severity="warning"
                  sx={{ mb: 1.5 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      sx={{ whiteSpace: 'nowrap' }}
                      onClick={() =>
                        testsApi(getIdToken, b.customer_id)
                          .continueBatch(b.id)
                          .then(() => {
                            setTestNotice(`Finishing ${targets.length} route${targets.length === 1 ? '' : 's'}; results already done are kept.`);
                            setTestsReload(r => r + 1);
                          })
                          .catch(e => setTestNotice(`Couldn't continue: ${e instanceof Error ? e.message : String(e)}`))
                      }
                    >
                      Finish them
                    </Button>
                  }
                >
                  {b.label}: {targets.length} route{targets.length === 1 ? '' : 's'} stopped at the credit cap (
                  {targets.map(t => t.target_name || t.target_id).join(', ')}), so their picks cover only part of the cases. Finishing runs only the
                  missing results, about {fmtNum(Math.round(credits))} more credits.
                </Alert>
              ))}
            {table(platformRows, false)}
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <SectionTitle title="Regional semantic search" help="semantic_search_<code> functions used when the Passio-Product-Localization header is set." />
            {table(regionalRows, true)}
          </Paper>
        </Box>
      )}
      <PlatformTestDialog
        open={testOpen}
        api={tests}
        targets={platformRows
          .filter(r => r.function_id && selected.has(routeKey(r.customer_id, r.function_name)))
          .map(r => ({ function_id: r.function_id as string, customer_id: r.customer_id, name: r.function_name, kind: r.kind, default_model: r.default_model }))}
        onClose={() => setTestOpen(false)}
        onStarted={batches => {
          setTestOpen(false);
          setSelected(new Set());
          const runs = batches.reduce((n, b) => n + (b.summary.runs || 0), 0);
          setTestNotice(`Testing ${runs} route${runs === 1 ? '' : 's'} in the background. Picks fill in as each finishes.`);
          setTestsReload(r => r + 1);
        }}
      />
      <ModelGroupsDialog
        open={groupsOpen}
        api={tests}
        onClose={() => setGroupsOpen(false)}
        onSaved={g => {
          setGroupsOpen(false);
          setGroups(g);
          setTestsReload(r => r + 1);
        }}
      />
      {platformTests && (
        <ScheduleDialog
          open={scheduleOpen}
          api={platformTests}
          scope="platform"
          schedule={schedule || null}
          onClose={() => setScheduleOpen(false)}
          onSaved={s => {
            setScheduleOpen(false);
            setSchedule(s);
          }}
        />
      )}
    </Box>
  );
};

export default AdminPlatformRoutes;
